import { createReducer } from '@reduxjs/toolkit';

import { GlobalState } from '../../../types.global';
import { SearchState } from './types';

import { isSameDay } from '../../../../shared';
import * as actions from './actions';
import { fetchEntities } from './thunks';
import { browserStorageUpdate } from '../../../store/actions';

const nonPersistentState = {
  highlighted: {
    id: null,
    inputType: null,
    mouse: null,
  },
  isFocused: false,
  isChooseEngineOpened: false,
  provided: '',
  command: null,
  isSubmitted: false,
  isSpeech: false,
  gainedShells: null,
  instantResults: {
    counter: 0,
    data: null,
    state: null,
    term: null,
  },

  searchTags: {
    term: null,
    activeId: null,
  },
  history: [],
  $organic: null,
  $entities: {
    state: null,
    zp: null,
    searchTags: [],
    popularWebsites: [],
    history: [],
    tabs: [],
  },
  $thinksuggestData: {
    provided: null,
    term: null,
    suggestions: null,
  },
};

export const INITIAL_STATE = {
  isSearchDiscovered: false,
  ntpSearchCount: 0,
  dailyAdClicksCount: 0,
  isAutofocus: null,
  isChooseEngineOpened: false,
  engine: null,
  hints: {
    history: [],
  },
  ...nonPersistentState,
};

export const getInitialSearchState = (
  persistedState: GlobalState
): SearchState => {
  if (!persistedState.search) return INITIAL_STATE;

  const isFirstTimeToday =
    !persistedState.user.openedAt ||
    !isSameDay(persistedState.user.openedAt, Date.now());

  const q =
    typeof window === 'undefined'
      ? null
      : new URLSearchParams(window.location.search).get('q');

  return {
    ...INITIAL_STATE,
    history: persistedState.search.history || [],
    isSearchDiscovered: persistedState.search.isSearchDiscovered || false,
    ntpSearchCount: persistedState.search.ntpSearchCount || 0,
    isAutofocus:
      typeof persistedState.search.isAutofocus === 'undefined'
        ? null
        : persistedState.search.isAutofocus,

    engine: (() => {
      // @ts-ignore // migration of old data, remove in the future
      const { engine } = persistedState.user;
      if (!persistedState.search.engine && engine) {
        return engine;
      }

      return persistedState.search.engine || INITIAL_STATE.engine;
    })(),
    dailyAdClicksCount: isFirstTimeToday
      ? 0
      : persistedState.search.dailyAdClicksCount || 0,
    ...(q && { provided: q }),
  };
};

const reducer = createReducer(null, (builder) => {
  builder
    .addCase(browserStorageUpdate, (state, { payload }) => {
      if (payload.isNewer) {
        return getInitialSearchState(payload.state);
      }
    })
    .addCase(actions.chooseEngine, (state, { payload }) => {
      state.engine = payload;
    })
    .addCase(actions.discoveredCommand, (state, { payload }) => {
      state.command = payload;
    })
    .addCase(actions.setIsAutofocus, (state, { payload }): void => {
      state.isAutofocus = payload;
    })
    .addCase(actions.toggledInputFocus, (state, { payload }) => {
      state.isFocused = payload;
      state.isSearchDiscovered = true;
    })
    .addCase(actions.speechStarted, (state: SearchState) => {
      state.isSpeech = true;
    })
    .addCase(actions.speechEnded, (state: SearchState) => {
      state.isSpeech = false;
    })
    .addCase(actions.pressedEscape, (state: SearchState, { payload }) => {
      state.isFocused = payload || false;
      const { term, activeId } = state.searchTags;

      if (activeId) {
        state.searchTags.activeId = null;
        state.provided = term;
      }
    })
    .addCase(actions.toggledChooseEngine, (state: SearchState, { payload }) => {
      state.isChooseEngineOpened =
        typeof payload === 'undefined' ? !state.isChooseEngineOpened : payload;
    })
    .addCase(actions.suggestionHover, (state: SearchState, { payload }) => {
      state.highlighted.mouse = payload;
    })
    .addCase(actions.searchTagSelected, (state: SearchState, { payload }) => {
      const existingSearchTagId = state.searchTags.activeId;
      const newSearchTagId = (state.searchTags.activeId =
        existingSearchTagId === payload ? null : payload);

      const currentSearchTagTerm = state.searchTags.term;
      const { provided } = state;

      if (currentSearchTagTerm === null) {
        state.searchTags.term = newSearchTagId ? provided || '' : null;
      }

      const isSearchTagTerm = typeof currentSearchTagTerm === 'string';

      if (
        ((!isSearchTagTerm && newSearchTagId) ||
          (isSearchTagTerm && !newSearchTagId)) &&
        !(currentSearchTagTerm && provided && currentSearchTagTerm !== provided)
      ) {
        state.provided = state.searchTags.activeId
          ? ''
          : provided || state.searchTags.term;
      }
    })
    .addCase(actions.suggestionNavigate, (state: SearchState, { payload }) => {
      state.isFocused = true;

      state.highlighted = {
        id: payload?.id,
        mouse: null,
        inputType: payload.inputType,
      };
    })
    .addCase(actions.searchSubmitted, (state: SearchState, { payload }) => {
      state.provided = payload.term;
      state.isSubmitted = true;
      state.ntpSearchCount++;
      state.gainedShells = payload.gain;
      state.isSpeech = false;

      if (
        !state.history.find(
          (term) => term.toLowerCase() === payload.term.toLowerCase()
        )
      ) {
        state.history = [payload.term, ...state.history].slice(0, 10);
      }

      if (payload.isAd) {
        state.dailyAdClicksCount++;
      }

      if ((payload.hintId ?? false) !== false) {
        state.hints.history.push(payload.hintId);
      }
    })
    .addCase(
      actions.userTyping,
      (state: SearchState, { payload: { inputType, term } }) => {
        state.highlighted.mouse = null;
        state.isFocused = true;
        if (!state.searchTags.activeId) {
          state.searchTags.term = null;
        }

        state.provided = term;
        if (!term || inputType === 'deleteContentBackward') {
          state.highlighted.id = null;
        }
      }
    )

    .addCase(actions.removeSuggestion, (state: SearchState, { payload }) => {
      state.history = payload;
    })

    .addCase(actions.fetchedSuggestions, (state: SearchState, { payload }) => {
      state.$organic = payload;
    })

    .addCase(
      actions.fetchedThinksuggestAds,
      (state: SearchState, { payload }) => {
        state.$thinksuggestData = payload;
      }
    )

    .addCase(fetchEntities.pending, (state: SearchState) => {
      state.$entities.state = 'PENDING';
    })
    .addCase(fetchEntities.fulfilled, (state: SearchState, { payload }) => {
      state.$entities.state = 'FULFILLED';
      state.$entities = {
        ...state.$entities,
        ...payload,
      };
    })
    .addCase(fetchEntities.rejected, (state: SearchState) => {
      state.$entities.state = 'FAILURE';
    })

    .addCase(
      actions.fetchedInstantResults.pending,
      (state: SearchState, { payload }) => {
        state.instantResults.state = 'PENDING';
        state.instantResults.counter++;
        state.instantResults.term = payload;

        state.$thinksuggestData.term = null;
        state.$thinksuggestData.suggestions = null;
      }
    )
    .addCase(
      actions.fetchedInstantResults.fulfilled,
      (state: SearchState, { payload }) => {
        state.instantResults.state = 'FULFILLED';
        state.instantResults.data = payload;
      }
    )
    .addCase(actions.fetchedInstantResults.rejected, (state: SearchState) => {
      state.instantResults.state = 'FAILURE';
      state.instantResults.data = null;
    });
});

// -----------------------------------------------------------------------------

export default reducer;
